import { Badge } from "@hortis/ui/badge";
import { Button, IconButton } from "@hortis/ui/button";
import { ContextMenu, ContextMenuTrigger } from "@hortis/ui/context-menu";
import { Divider } from "@hortis/ui/divider";
import { Dot } from "@hortis/ui/dot";
import {
  ArrowsDown,
  ArrowsUp,
  Plus,
  SearchMd,
  Settings4,
  XClose,
} from "@hortis/ui/icons";
import { MarkerPinOff1 } from "@hortis/ui/icons/marker-pin-off-1";
import { Input } from "@hortis/ui/input";
import { IUCNRedListTag } from "@hortis/ui/iucn-red-list-tag";
import { Label } from "@hortis/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { CircularProgress } from "@hortis/ui/progress";
import { ScrollArea, ScrollAreaViewport } from "@hortis/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@hortis/ui/select";
import { Switch } from "@hortis/ui/switch";
import { Tag } from "@hortis/ui/tag";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { Link } from "@tanstack/react-router";
import { MaterialFieldsFragment } from "generated/gql/graphql";
import {
  LocationFieldsFragment,
  PlantMaterialStatus,
  SortOrder,
} from "generated/graphql";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCreateMaterialStore } from "src/components/dialog/variants/create-material-dialog";
import { PlantMaterialStatusToColor } from "src/components/status-chip";
import { useHoveredListMaterialStore } from "src/features/collection-map/components/hovered-list-material-store";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { MaterialsContextMenuContent } from "src/features/context-menu/variants/materials-context-menu";
import {
  RecordCard,
  RecordCardContent,
  RecordCardPhoto,
  RecordCardProperties,
  RecordCardProperty,
  RecordCardTitle,
} from "src/features/records/components/new-record-card";
import { getThumbnailPhotoUrl } from "src/utils/photos";
import { twMerge } from "tailwind-merge";

export type MaterialSortColumn = "number" | "scientificName";

export const LocationMaterialsSection = ({
  location,
  showAbsentMaterials,
  setShowAbsentMaterials,
  searchQuery,
  setSearchQuery,
  sortOrder,
  setSortOrder,
  sortColumn,
  setSortColumn,
  isFetching = false,
}: {
  location: LocationFieldsFragment;
  showAbsentMaterials: boolean;
  setShowAbsentMaterials: Dispatch<SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  sortOrder: SortOrder;
  setSortOrder: Dispatch<SetStateAction<SortOrder>>;
  sortColumn: MaterialSortColumn;
  setSortColumn: Dispatch<SetStateAction<MaterialSortColumn>>;
  isFetching?: boolean;
}) => {
  const { setHoveredListMaterial } = useHoveredListMaterialStore();
  const materials = location.material?.nodes;
  const { setCreateMaterialDialogOpen } = useCreateMaterialStore();
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchToggleRef = useRef<HTMLButtonElement>(null);

  const handleMouseEnter = useCallback(
    (
      row: Pick<MaterialFieldsFragment, "id" | "qualifier"> & {
        accession?: { accessionNumber: string } | null;
      },
    ) => {
      setHoveredListMaterial({
        id: row.id,
        materialQualifier: row.qualifier,
        accessionNumber: row.accession?.accessionNumber ?? "",
      });
    },
    [setHoveredListMaterial],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredListMaterial(undefined);
  }, [setHoveredListMaterial]);

  const handleToggleSearch = () => {
    setShowSearch((prev) => {
      if (prev) {
        setSearchQuery("");
        setTimeout(() => {
          searchToggleRef.current?.focus();
        }, 50);
      } else {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 50);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (searchQuery === "" && document.activeElement !== inputRef.current) {
      setShowSearch(false);
    }
  }, [searchQuery]);

  return (
    <div>
      <div className="py-2 px-6 border-y border-grey-200 flex items-center justify-between sticky -top-px bg-white z-10">
        <div className="font-medium text-grey-900 text-base flex items-center gap-2">
          Materials
          {materials != null && (
            <Badge color="grey" className="rounded-md" testId="materials-count">
              {materials?.length}
            </Badge>
          )}
          {isFetching && <CircularProgress size={16} className="ml-1" />}
        </div>
        <div className="flex items-center gap-1">
          <div
            className="relative"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Handle search logic here
              }
              if (e.key === "Escape") {
                handleToggleSearch();
              }
            }}
          >
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={twMerge(
                "text-base w-40 [&_input]:w-24! transition-all relative -mx-px pr-6",
                !showSearch &&
                  "w-20 [&_input]:w-0 border-transparent shadow-none opacity-0 invisible",
              )}
              placeholder="Search"
              ref={inputRef}
              onBlur={() => {
                if (searchQuery === "") {
                  setShowSearch(false);
                }
              }}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  icon={showSearch ? XClose : SearchMd}
                  size="sm"
                  variant="tertiaryGray"
                  ariaLabel={showSearch ? "Clear search" : "Search materials"}
                  className="absolute right-0 top-1/2 -translate-y-1/2"
                  onClick={handleToggleSearch}
                  ref={searchToggleRef}
                  onFocusCapture={stopEventPropagation}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {showSearch ? "Clear search" : "Search materials"}
              </TooltipContent>
            </Tooltip>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <IconButton
                icon={Settings4}
                size="sm"
                variant="tertiaryGray"
                ariaLabel="View settings"
              />
            </PopoverTrigger>
            <PopoverContent align="end" className="w-xs!">
              <div className="p-4">
                <div className="flex items-center gap-2 justify-between">
                  <span className="text-sm font-medium text-grey-500">
                    Ordering
                  </span>
                  <div className="flex items-center gap-2">
                    <Select
                      onValueChange={(value) => {
                        setSortColumn(value as MaterialSortColumn);
                      }}
                      value={sortColumn}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Order" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scientificName">
                          Scientific name
                        </SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <IconButton
                          ariaLabel="Switch"
                          icon={
                            sortOrder === SortOrder.Ascending
                              ? ArrowsDown
                              : ArrowsUp
                          }
                          size="sm"
                          onClick={() => {
                            setSortOrder((prev) =>
                              prev === SortOrder.Ascending
                                ? SortOrder.Descending
                                : SortOrder.Ascending,
                            );
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {sortOrder === SortOrder.Ascending
                          ? "Ascending"
                          : "Descending"}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <Divider />
              <div className="p-4">
                <Label className="flex items-center justify-between text-grey-500">
                  Show absent
                  <Switch
                    data-cy="show-absent-toggle"
                    size="xs"
                    onCheckedChange={(checked) => {
                      setShowAbsentMaterials(checked);
                    }}
                    checked={showAbsentMaterials}
                  />
                </Label>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <ScrollArea>
        <ScrollAreaViewport>
          <div className="p-3 space-y-px">
            {materials != null && materials.length > 0 ? (
              materials.map((material) => (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <Link
                      key={material.id}
                      to="map/material/$materialNumber"
                      from="/site/$siteSlug"
                      params={{
                        materialNumber: createMaterialNumber(
                          material.accession?.accessionNumber ?? "-",
                          material.qualifier,
                        ),
                      }}
                      className="focus-visible:[&>div]:bg-grey-50 outline-none data-[state=open]:[&>div]:bg-grey-50"
                      onMouseEnter={() => handleMouseEnter(material)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <RecordCard
                        size="sm"
                        className={twMerge(
                          material.status === PlantMaterialStatus.Absent &&
                            "grayscale",
                        )}
                        testId="material-record-card"
                      >
                        <RecordCardPhoto
                          photo={getThumbnailPhotoUrl(material)}
                        />
                        <RecordCardContent className="truncate">
                          <RecordCardTitle className="truncate">
                            <span className="truncate shrink-0">
                              {material.accession?.taxon?.scientificName}
                            </span>
                            <RecordCardProperty className="inline">
                              {material.accession?.taxon?.commonName}
                            </RecordCardProperty>
                          </RecordCardTitle>
                          <RecordCardProperties>
                            {material.accession?.taxon != null && (
                              <RecordCardProperty className="gap-1.5">
                                <Dot
                                  color={
                                    PlantMaterialStatusToColor[material.status]
                                  }
                                />
                                {createMaterialNumber(
                                  material.accession?.accessionNumber,
                                  material.qualifier,
                                  "/",
                                )}
                              </RecordCardProperty>
                            )}
                            {material.position == null && (
                              <MarkerPinOff1
                                className="h-4 w-4 shrink-0 text-grey-500"
                                data-cy="missing-position-icon"
                              />
                            )}
                            {material.accession?.taxon?.sharedScientificName
                              ?.taxon?.globalRedListStatus != null &&
                              material.accession.taxon.fullyValidated ===
                                true && (
                                <IUCNRedListTag
                                  status={
                                    material.accession?.taxon
                                      .sharedScientificName?.taxon
                                      ?.globalRedListStatus
                                  }
                                  short
                                  className="text-xs py-px px-1 rounded-md"
                                />
                              )}
                            <Tag className="border-grey-100 bg-grey-100 text-xs py-px px-1 rounded-md">
                              {material.accession?.provenanceCategoryCode}
                            </Tag>
                          </RecordCardProperties>
                        </RecordCardContent>
                      </RecordCard>
                    </Link>
                  </ContextMenuTrigger>
                  <MaterialsContextMenuContent
                    selectedRecordsIDs={[]}
                    clickedRow={material}
                  />
                </ContextMenu>
              ))
            ) : (
              <div
                data-cy="empty-materials"
                className="font-medium text-base text-grey-900 flex flex-col items-center justify-center py-4 gap-3"
              >
                No materials
                {searchQuery === "" ? (
                  <Button
                    startIcon={Plus}
                    className="rounded-full"
                    size="sm"
                    onClick={() =>
                      setCreateMaterialDialogOpen(true, {
                        collectionSiteLocationOption: {
                          id: location.id,
                          name: location.name,
                        },
                      })
                    }
                  >
                    New material
                  </Button>
                ) : (
                  <Button
                    startIcon={XClose}
                    className="rounded-full"
                    size="sm"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear search
                  </Button>
                )}
              </div>
            )}
          </div>
        </ScrollAreaViewport>
      </ScrollArea>
    </div>
  );
};
